import { PageTitle } from "@/components/custom/PageTitle"
import { DataTable } from "./OrganizationsTable"
import { defaultColumns, activeOnlyColumns } from "./columns"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { RootState } from "@/redux"
import { useEffect, useMemo, useState } from "react"
import { Switch } from "@/components/ui/switch"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Pagination } from "@/components/custom/Pagination/Pagination"
import { INPUT_FIELD_CLASSNAME } from "@/constants/classname/classname"
import { Delete } from "lucide-react"
import { ButtonVariant } from "@/constants/button/button"

const DEFAULT_PAGE_SIZE = 10

export const Organizations = () => {
  const navigate = useNavigate()
  const { organizations } = useSelector((state: RootState) => state.organization)
  const [showActiveOnly, setShowActiveOnly] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [paginationState, setPaginationState] = useState({ limit: DEFAULT_PAGE_SIZE, offset: 0 })

  const toShowColumns = showActiveOnly ? activeOnlyColumns : defaultColumns

  const sortedOrganizations = useMemo(() => {
    if (!organizations) return []
    return [...organizations].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }))
  }, [organizations])

  const filteredOrganizations = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    if (!normalizedQuery) return sortedOrganizations
    return sortedOrganizations.filter(org => (org.name ?? "").toLowerCase().includes(normalizedQuery))
  }, [sortedOrganizations, searchQuery])

  useEffect(() => {
    setPaginationState(prev => {
      const total = filteredOrganizations.length
      if (total === 0) {
        return prev.offset === 0 ? prev : { ...prev, offset: 0 }
      }
      const lastPageOffset = Math.floor((total - 1) / prev.limit) * prev.limit
      if (prev.offset > lastPageOffset) {
        return { ...prev, offset: lastPageOffset }
      }
      return prev
    })
  }, [filteredOrganizations.length, paginationState.limit])

  const paginatedOrganizations = useMemo(() => {
    const start = paginationState.offset
    const end = start + paginationState.limit
    return filteredOrganizations.slice(start, end)
  }, [filteredOrganizations, paginationState])

  return (
    <div className="protectedPageLayout">
      <PageTitle
        title="Organizations"
        buttonText="Add New Organization"
        onButtonClick={() => { navigate('/organizations/create'); }}
      />
      <div className="w-full flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
        <div className="flex w-full items-center gap-2 md:max-w-md relative">
          <Input
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value)
              setPaginationState(prev => ({ ...prev, offset: 0 }))
            }}
            placeholder="Search organizations"
            className={`${INPUT_FIELD_CLASSNAME} bg-foreground text-primary`}
            aria-label="Search organizations"
          />
          {searchQuery && (
            <Button
              type="button"
              variant={ButtonVariant.ICON}
              className="h-9 text-destructive rounded rounded-md bg-destructive-foreground absolute right-[2px]"
              onClick={() => setSearchQuery("")}
              disabled={!searchQuery.trim()}
            >
              <Delete size={16} />
            </Button>
          )}
        </div>
        <div className="w-full justify-end flex items-center md:w-auto">
          <p className="text-primary mr-2 text-sm">Show Active Workflows Only</p>
          <Switch
            id="active-only"
            checked={showActiveOnly}
            onCheckedChange={() => setShowActiveOnly(!showActiveOnly)}
            className="cursor-pointer"
          />
        </div>
      </div>
      <DataTable columns={toShowColumns} data={paginatedOrganizations} />
      <Pagination
        total={filteredOrganizations.length}
        limit={paginationState.limit}
        offset={paginationState.offset}
        onPageChange={(newOffset) => setPaginationState(prev => ({ ...prev, offset: newOffset }))}
        onLimitChange={(newLimit) => setPaginationState({ limit: newLimit, offset: 0 })}
      />
    </div>
  )
}
